import { Validators, type ValidationErrors } from '@angular/forms';
import { Money } from '@principle-theorem/accounting';
import {
  isMixedSchema,
  getSchemaText,
  type MixedSchema,
} from '@principle-theorem/editor';
import { TypedFormControl, TypedFormGroup } from '@principle-theorem/ng-shared';
import {
  DEFAULT_CANCELLATION_FEE_ATTRIBUTION,
  DepositSettings,
  Practice,
} from '@principle-theorem/principle-core';
import {
  CancellationFeeAttribution,
  DepositCaptureStrategy,
  DepositForfeiturePolicy,
  DepositRequestStatus,
  type IAppointment,
  type IBrand,
  type IDepositForfeitureDecision,
  type IDepositRequest,
  type ISchedulingEventReason,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  isSameRef,
  undeletedFilter,
  where,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { first } from 'lodash';

export interface IDepositForfeitureContext {
  depositRequest: WithRef<IDepositRequest>;
  policy: DepositForfeiturePolicy;
  percentage: number;
  defaultShouldForfeit: boolean;
  defaultAmountCents: number;
  defaultFeeAttribution: CancellationFeeAttribution;
}

export interface IFeeAttributionOption {
  value: CancellationFeeAttribution;
  label: string;
  isDefault: boolean;
}

/**
 * Everything the fee attribution line renders, resolved in one place so the
 * template never has to work out labels or whether the default is in use.
 */
export interface IFeeAttributionView {
  selected: CancellationFeeAttribution;
  recipientInSentence: string;
  isDefault: boolean;
  options: IFeeAttributionOption[];
}

export interface IDepositForfeitureFormValue {
  shouldForfeit: boolean;
  amountDollars?: number;
  feeAttribution: CancellationFeeAttribution;
  overrideReason?: MixedSchema;
}

/**
 * Shared by `Validators.min` and the input's `min` attribute so the native
 * control and the reactive form can never disagree about the floor.
 */
export const MINIMUM_FORFEIT_AMOUNT_DOLLARS = 0.01;

/**
 * Owns the form, context, and decision rules for a deposit forfeiture so the
 * cancellation flow applies the configured policy consistently.
 */
export class DepositForfeiture {
  /**
   * Creates one strongly typed form shape so policy-dependent validation is
   * applied to the same controls wherever a forfeiture is edited.
   */
  static createDepositForfeitureForm(): TypedFormGroup<IDepositForfeitureFormValue> {
    return new TypedFormGroup<IDepositForfeitureFormValue>({
      shouldForfeit: new TypedFormControl(false),
      amountDollars: new TypedFormControl<number | undefined>(undefined),
      feeAttribution: new TypedFormControl<CancellationFeeAttribution>(
        DEFAULT_CANCELLATION_FEE_ATTRIBUTION
      ),
      overrideReason: new TypedFormControl<MixedSchema | undefined>(undefined),
    });
  }

  /**
   * Resolves the applicable policy and settled request before presenting a
   * decision, preventing forfeiture controls for ineligible deposits.
   */
  static async resolveDepositForfeitureContext(
    appointment: WithRef<IAppointment>,
    brand: WithRef<IBrand>,
    reasonRef?: DocumentReference<ISchedulingEventReason>
  ): Promise<IDepositForfeitureContext | undefined> {
    const rawSettings = brand.settings.onlineBookings?.deposit;
    if (!reasonRef || !rawSettings) {
      return;
    }
    const settings = DepositSettings.withDefaults(rawSettings);
    const rule = settings.forfeitureRules.find((candidate) =>
      isSameRef(candidate.reasonRef, reasonRef)
    );
    if (!rule) {
      return;
    }
    // Filtered after the read, like the forfeiture handler, so frontend and
    // backend resolve the same request without a further composite index.
    const requests = (
      await Firestore.getDocs(
        Practice.depositRequestCol(appointment.practice),
        where('appointmentRef', '==', appointment.ref)
      )
    ).filter(undeletedFilter);
    const depositRequest = first(
      requests.filter(
        (request) =>
          request.status === DepositRequestStatus.Paid ||
          (request.status === DepositRequestStatus.Authorised &&
            request.captureStrategy ===
              DepositCaptureStrategy.CaptureOnCancellationOrReschedule)
      )
    );
    if (!depositRequest) {
      return;
    }
    return {
      depositRequest,
      policy: settings.forfeiturePolicy,
      percentage: rule.forfeiturePercentage,
      defaultShouldForfeit:
        settings.forfeiturePolicy === DepositForfeiturePolicy.Auto,
      defaultAmountCents: Money.percentageOfCents(
        depositRequest.amountCents,
        rule.forfeiturePercentage
      ),
      defaultFeeAttribution: settings.cancellationFeeAttribution,
    };
  }

  /**
   * The ceiling a staffer may forfeit, in dollars. Feeds both `Validators.max`
   * and the input's `max` attribute from one place, so the field the staffer
   * types into and the form that gates the save agree on the limit.
   */
  static maximumForfeitAmountDollars(
    context: IDepositForfeitureContext
  ): number {
    return Money.fromCents(context.depositRequest.amountCents);
  }

  /**
   * Aligns the editable form with the active policy so required amounts and
   * explanations cannot be bypassed by a policy override.
   */
  static configureDepositForfeitureValidators(
    form: TypedFormGroup<IDepositForfeitureFormValue>,
    context?: IDepositForfeitureContext
  ): boolean {
    form.controls.amountDollars.clearValidators();
    form.controls.overrideReason.clearValidators();
    if (!context) {
      return false;
    }
    if (form.controls.shouldForfeit.value) {
      form.controls.amountDollars.setValidators([
        Validators.required,
        Validators.min(MINIMUM_FORFEIT_AMOUNT_DOLLARS),
        Validators.max(DepositForfeiture.maximumForfeitAmountDollars(context)),
      ]);
    } else if (context.defaultShouldForfeit) {
      form.controls.overrideReason.setValidators([
        (control): ValidationErrors | null => {
          const value: unknown = control.value;
          if (
            !isMixedSchema(value) ||
            getSchemaText(value, {
              includeLinkUrls: true,
              paragraphSeparator: '\n\n',
            }).length === 0
          ) {
            return { required: true };
          }
          // eslint-disable-next-line no-null/no-null -- Angular Validator requires null for a valid control.
          return null;
        },
      ]);
    }
    form.controls.amountDollars.updateValueAndValidity({ emitEvent: false });
    form.controls.overrideReason.updateValueAndValidity({ emitEvent: false });
    return context.defaultShouldForfeit && !form.controls.shouldForfeit.value;
  }

  /**
   * Names the practitioner in the summary so staff know exactly who gets the
   * fee, and flags the brand default so choosing it again is the way to undo.
   */
  static describeFeeAttribution(
    selected: CancellationFeeAttribution,
    defaultAttribution: CancellationFeeAttribution,
    practitionerName?: string
  ): IFeeAttributionView {
    const optionLabels: Record<CancellationFeeAttribution, string> = {
      [CancellationFeeAttribution.Practice]: 'Practice',
      [CancellationFeeAttribution.Practitioner]: 'Practitioner',
    };
    const recipients: Record<CancellationFeeAttribution, string> = {
      [CancellationFeeAttribution.Practice]: 'the practice',
      [CancellationFeeAttribution.Practitioner]:
        practitionerName ?? `the appointment's practitioner`,
    };
    const options = [
      CancellationFeeAttribution.Practice,
      CancellationFeeAttribution.Practitioner,
    ].map((value) => ({
      value,
      label: optionLabels[value],
      isDefault: value === defaultAttribution,
    }));
    return {
      selected,
      recipientInSentence: recipients[selected],
      isDefault: selected === defaultAttribution,
      options,
    };
  }

  /**
   * Omits unchanged defaults so persisted decisions record only a deliberate
   * staff override of the configured forfeiture policy.
   */
  static toDepositForfeitureDecision(
    value: IDepositForfeitureFormValue,
    context: IDepositForfeitureContext
  ): IDepositForfeitureDecision | undefined {
    const amountCents = Money.toCents(value.amountDollars ?? 0);
    const usesDefaultForfeitTerms =
      amountCents === context.defaultAmountCents &&
      value.feeAttribution === context.defaultFeeAttribution;
    const usesDefault =
      value.shouldForfeit === context.defaultShouldForfeit &&
      (!value.shouldForfeit || usesDefaultForfeitTerms);
    if (usesDefault) {
      return;
    }

    const base = {
      depositRequestRef: context.depositRequest.ref,
      policyAtDecision: context.policy,
      configuredPercentage: context.percentage,
      defaultShouldForfeit: context.defaultShouldForfeit,
      overrideReason: value.overrideReason,
    };
    return value.shouldForfeit
      ? {
          ...base,
          shouldForfeit: true,
          amountCents,
          feeAttribution: value.feeAttribution,
        }
      : { ...base, shouldForfeit: false };
  }
}
